import { useState } from "react";
import { useEffect } from "react";
import { PolarArea } from "react-chartjs-2";
import { Doughnut } from "react-chartjs-2";

import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ArcElement, Chart as ChartJS, Legend, RadialLinearScale, Tooltip } from "chart.js";
import "chart.js/auto";

import constants from "../../assets/constants";
import { capitalizeAllWords } from "../../utils/helpers.js";
import { getGraphColor } from "./graphUtils";

ChartJS.register(RadialLinearScale, ArcElement, Tooltip, Legend);

const BotpressConversationsGraph = ({
  chatsAnalysis,
  botpressGraphType = constants.botpressGraphTypes.conversationTopicsGraph,
  colorIndex = 0,
}) => {
  const theme = useTheme();

  const generateGraphData = (theChatsAnalysis, graphType) => {
    if (theChatsAnalysis == null) {
      return null;
    }

    const labels = [];
    const dataSets = [];

    if (graphType === constants.botpressGraphTypes.conversationTopicsGraph) {
      let x = colorIndex;
      const data = [];
      const colors = [];
      const borderColors = [];
      let index = 0;
      for (const [key, value] of theChatsAnalysis.topicFrequency) {
        if (index === 10) {
          break;
        }
        // its pre sorted
        labels.push(`${capitalizeAllWords(key)}: ${value}`);
        data.push(value);
        colors.push(getGraphColor(theme, x, 0.55));
        borderColors.push(getGraphColor(theme, x++));
        ++index;
      }
      dataSets.push({
        label: "Topics",
        data: data,
        borderColor: borderColors,
        backgroundColor: colors,
        borderWidth: 1,
        fill: true,
      });
    } else if (graphType === constants.botpressGraphTypes.conversationSentimentGraph) {
      // let x = colorIndex;
      const data = [];
      const colors = [];
      const borderColors = [];
      for (const [key, value] of theChatsAnalysis.sentimentFrequency) {
        // its pre sorted
        labels.push(`${capitalizeAllWords(key)}: ${value}`);
        data.push(value);
      }

      for (let index = 0; index < labels.length; ++index) {
        let theColor = theme.palette.error.main;
        if (labels[index].startsWith("Neutral")) {
          theColor = theme.palette.info.main;
        } else if (labels[index].startsWith("Positive")) {
          theColor = theme.palette.success.main;
        }
        colors.push(theColor);
        borderColors.push(theColor);
      }

      dataSets.push({
        label: "Sentiment",
        data: data,
        borderColor: borderColors,
        backgroundColor: colors,
        borderWidth: 1,
        fill: true,
      });
    }
    return {
      labels: labels,
      datasets: dataSets,
    };
  };

  const [data, setData] = useState(null);

  const generateOptions = graphType => {
    if (graphType == null) {
      return null;
    }

    switch (graphType) {
      case constants.botpressGraphTypes.conversationTopicsGraph: {
        return {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
            title: {
              display: false,
            },
          },
          scales: {
            r: {
              ticks: {
                backdropColor: "transparent",
              },
            },
          },
        };
      }
      case constants.botpressGraphTypes.conversationSentimentGraph: {
        return {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "left",
              align: "top",
              boxHeight: 48,
              font: {
                size: 48, // Set the desired font size in pixels
              },
              labels: {
                font: {
                  size: 24, // Set the legend font size
                },
              },
            },
            title: {
              display: false,
            },
          },
        };
      }
      // case constants.botpressGraphTypes.sessionsGraph: {
      //   return {
      //     responsive: true,
      //     maintainAspectRatio: false,
      //     plugins: {
      //       legend: {
      //         position: "top",
      //       },
      //       title: {
      //         display: false,
      //       },
      //     },
      //     scales: {
      //       y: {
      //         ticks: {
      //           // Enforces a step of 1, ensuring only integers are displayed
      //           stepSize: 1,
      //           // Alternately, use a callback function to only return whole numbers
      //           // callback: function(value, index, ticks) {
      //           //   if (Math.floor(value) === value) {
      //           //     return value;
      //           //   }
      //           // }
      //         },
      //       },
      //     },
      //   };
      // }
      default:
    }
  };

  const [options, setOptions] = useState(null);

  useEffect(() => {
    setData(generateGraphData(chatsAnalysis, botpressGraphType));
    setOptions(generateOptions(botpressGraphType));
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatsAnalysis, botpressGraphType]);

  if (!data || !options) {
    return <Box sx={{ position: "relative" }}>No data available</Box>;
  }

  if (botpressGraphType === constants.botpressGraphTypes.conversationTopicsGraph) {
    const dataset = data.datasets[0] || {};

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          height: "100%",
          width: "100%",
          gap: theme.spacing(2),
        }}
      >
        <Box
          sx={{
            flex: "0 1 auto",
            minWidth: "35%",
            maxWidth: "55%",
            maxHeight: "100%",
            overflowY: "auto",
            paddingRight: theme.spacing(1),
          }}
        >
          {data.labels.map((label, legendIndex) => (
            <Box
              key={`${label}-${legendIndex}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(1),
                marginBottom: theme.spacing(0.75),
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  flexShrink: 0,
                  borderRadius: theme.spacing(0.5),
                  backgroundColor: dataset.backgroundColor?.[legendIndex],
                  border: `1px solid ${dataset.borderColor?.[legendIndex]}`,
                }}
              />
              <Typography
                sx={{
                  fontSize: "1rem",
                  lineHeight: 1.2,
                  color: theme.palette.text.primary,
                  wordBreak: "break-word",
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ position: "relative", flex: "1 1 auto", minWidth: 0, height: "100%" }}>
          <PolarArea options={options} data={data} />
        </Box>
      </Box>
    );
  } else if (botpressGraphType === constants.botpressGraphTypes.conversationSentimentGraph) {
    return <Doughnut options={options} data={data} />;
  }
  return <></>;
};

export default BotpressConversationsGraph;
